import React, { useState } from 'react'
import { RecipeRecord, ICONS, getIconSize, useSubscription } from '@talktoast/shared'

interface RecipeDetailModalProps {
  recipe: RecipeRecord | null
  isOpen: boolean
  onClose: () => void
  onRate?: (recipeId: string, rating: -1 | 0 | 1) => void
  onToggleFavorite?: (recipeId: string) => void
  onNavigateToSubscription?: () => void
}

export const RecipeDetailModal: React.FC<RecipeDetailModalProps> = ({
  recipe,
  isOpen,
  onClose,
  onRate,
  onToggleFavorite,
  onNavigateToSubscription
}) => {
  const [rating, setRating] = useState<-1 | 0 | 1>(0)
  const [isFavorite, setIsFavorite] = useState(false)
  const { loading: subscriptionLoading } = useSubscription()

  if (!isOpen || !recipe) return null

  const recipeData = recipe.recipe_data

  const handleThumbsUp = () => {
    const newRating = rating === 1 ? 0 : 1
    setRating(newRating)
    onRate?.(recipe.id, newRating)
  }

  const handleThumbsDown = () => {
    const newRating = rating === -1 ? 0 : -1
    setRating(newRating)
    onRate?.(recipe.id, newRating)

    // Clear favorite if thumbs down
    if (newRating === -1 && isFavorite) {
      setIsFavorite(false)
      onToggleFavorite?.(recipe.id)
    }
  }

  const handleToggleFavorite = () => {
    // Can't favorite a thumbs-downed recipe
    if (rating === -1 && !isFavorite) return

    const newFavoriteStatus = !isFavorite
    setIsFavorite(newFavoriteStatus)
    onToggleFavorite?.(recipe.id)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{recipeData.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none p-2 -m-2"
            aria-label="Close recipe details"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Description */}
          {recipeData.description && (
            <div>
              <p className="text-gray-600">{recipeData.description}</p>
            </div>
          )}

          {/* Timing & Nutrition */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-500">Prep Time</p>
              <p className="font-medium">{recipeData.prepTime ? `${recipeData.prepTime} min` : 'N/A'}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-500">Cook Time</p>
              <p className="font-medium">{recipeData.cookTime ? `${recipeData.cookTime} min` : 'N/A'}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-500">Servings</p>
              <p className="font-medium">{recipeData.servings || 'N/A'}</p>
            </div>
            {recipeData.nutrition?.calories && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Calories</p>
                <p className="font-medium">{recipeData.nutrition.calories}</p>
              </div>
            )}
          </div>

          {/* Ingredients */}
          {recipeData.ingredients && recipeData.ingredients.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Ingredients</h3>
              <ul className="space-y-1">
                {recipeData.ingredients.map((ingredient: any, idx: number) => (
                  <li key={idx} className="text-gray-700">
                    • {typeof ingredient === 'string'
                        ? ingredient
                        : (() => {
                            // Strip any leading quantity the AI may have erroneously placed in the
                            // name field (e.g. "1.5 lbs chicken") so it isn't doubled alongside
                            // the amount + unit fields (issue #112).
                            const rawName: string = ingredient.name || ''
                            const cleanName = rawName.replace(
                              /^\d+(?:[./]\d+)?\s*(?:lbs?|oz|g|kg|ml|l|cups?|tbsp|tsp|tablespoons?|teaspoons?|pieces?|cloves?|slices?|cans?|heads?|bunches?|pounds?|ounces?|grams?|kilograms?|millilitres?|litres?|pinch(?:es)?|handful|handfuls)?\s+/i,
                              ''
                            ).trim()
                            return `${ingredient.amount || ''} ${ingredient.unit || ''} ${cleanName}`.trim()
                          })()}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Instructions */}
          {recipeData.instructions && recipeData.instructions.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Instructions</h3>
              <ol className="space-y-2">
                {recipeData.instructions.map((instruction: string, idx: number) => (
                  <li key={idx} className="text-gray-700">
                    <span className="font-medium">{idx + 1}.</span> {instruction}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Nutrition Details */}
          {recipeData.nutrition && (
            <div>
              <h3 className="font-semibold mb-2">Nutrition (per serving)</h3>
              {recipeData.nutrition?.protein != null ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {recipeData.nutrition.calories && (
                    <div className="bg-gray-50 p-2 rounded text-center">
                      <p className="text-xs text-gray-500">Calories</p>
                      <p className="font-medium">{recipeData.nutrition.calories}</p>
                    </div>
                  )}
                  <div className="bg-gray-50 p-2 rounded text-center">
                    <p className="text-xs text-gray-500">Protein</p>
                    <p className="font-medium">{recipeData.nutrition.protein}g</p>
                  </div>
                  {recipeData.nutrition.carbs != null && (
                    <div className="bg-gray-50 p-2 rounded text-center">
                      <p className="text-xs text-gray-500">Carbs</p>
                      <p className="font-medium">{recipeData.nutrition.carbs}g</p>
                    </div>
                  )}
                  {recipeData.nutrition.fat != null && (
                    <div className="bg-gray-50 p-2 rounded text-center">
                      <p className="text-xs text-gray-500">Fat</p>
                      <p className="font-medium">{recipeData.nutrition.fat}g</p>
                    </div>
                  )}
                </div>
              ) : !subscriptionLoading ? (
                <div className="inline-flex items-center gap-1.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
                  <span>🔒</span>
                  <span>Macros available on Pro —</span>
                  <button
                    onClick={() => { onClose(); onNavigateToSubscription?.() }}
                    className="font-medium underline underline-offset-2 hover:text-amber-900"
                  >
                    Upgrade
                  </button>
                </div>
              ) : null}
            </div>
          )}

          {/* Rating & Favorite Section */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3">Rate This Recipe</h3>
            <div className="space-y-2">
              {/* Thumbs Up/Down Row */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleThumbsUp}
                  aria-label={rating === 1 ? 'Remove like rating' : 'Like this recipe'}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors flex-1 ${
                    rating === 1
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-600'
                  }`}
                >
                  <span className="text-xl">👍</span>
                  <span className="font-medium">Like</span>
                </button>

                <button
                  onClick={handleThumbsDown}
                  aria-label={rating === -1 ? 'Remove dislike rating' : 'Dislike this recipe'}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors flex-1 ${
                    rating === -1
                      ? 'bg-red-100 text-red-700 border border-red-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600'
                  }`}
                >
                  <span className="text-xl">👎</span>
                  <span className="font-medium">Dislike</span>
                </button>
              </div>

              {/* Favorite Button */}
              <button
                onClick={handleToggleFavorite}
                disabled={rating === -1 && !isFavorite}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                  isFavorite
                    ? 'bg-pink-100 text-pink-700 border border-pink-300'
                    : rating === -1
                    ? 'bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-600 hover:bg-pink-50 hover:text-pink-700 border border-gray-200'
                }`}
                title={rating === -1 && !isFavorite ? 'Cannot favorite a thumbs-downed recipe' : ''}
              >
                <ICONS.food.heart className={getIconSize('sm')} />
                <span className="font-medium">{isFavorite ? 'Favorited' : 'Add to Favorites'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
